import { RcButton } from '@/components/molecules/interactive/RcButton'
import { RcTrans } from '@/components/atoms/RcTrans'
import { RcIcon } from '@/components/atoms/RcIcon'
import { MatchCard } from '@/components/pages/Competition/components/match/card/MatchCard'
import { RcFileInput } from '@/components/molecules/input/RcFileInput'
import { ImageDisplay } from '@/components/atoms/ImageDisplay'
import { MatchResultFlowActions } from './MatchResultFlowActions'
import { MatchResultsMachine } from './match-results-machine'
import Box from '@mui/material/Box'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Grid from '@mui/material/Grid'
import LinearProgress from '@mui/material/LinearProgress'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import Alert from '@mui/material/Alert'
import IconButton from '@mui/material/IconButton'
import Chip from '@mui/material/Chip'
import { Container, Draggable } from 'react-smooth-dnd'
import { useState, useCallback, useEffect, useMemo } from 'react'
import { v4 as uuidv4 } from 'uuid'
import { WinCondition } from '@rallycry/api-suite-typescript'
import { useCompetitionTeamMembers } from '@/entity/competition-team/useCompetitionTeamMembers'

interface UploadedScreenshot {
  id: string
  url: string
  file: File
  isAnalyzing: boolean
  analysisResult?: any
  error?: string
  gameSlot?: number
}

export const UploadScreenshots = ({
  machine
}: {
  machine: MatchResultsMachine
}) => {
  const [state, send] = machine
  const { match, bracketAssignment1, bracketAssignment2, bracketSettings } =
    state.context
  const [screenshots, setScreenshots] = useState<UploadedScreenshot[]>([])

  // Calculate number of game slots needed based on win condition
  const getRequiredSlots = () => {
    const winConditionAmount = match?.winConditionAmount || 1
    if (bracketSettings?.winCondition === WinCondition.BEST_OF) {
      return winConditionAmount
    } else {
      // First to X, so max possible games is (X * 2) - 1
      return winConditionAmount * 2 - 1
    }
  }

  const requiredSlots = getRequiredSlots()

  // Get all player handles/usernames for both bracket assignments
  const bracketAssignment1Id = bracketAssignment1?.id
  const bracketAssignment2Id = bracketAssignment2?.id
  const entryId1 = bracketAssignment1?.entry?.id
  const entryId2 = bracketAssignment2?.entry?.id

  const { roster: bracketAssignment1Roster } = useCompetitionTeamMembers(
    entryId1 ? { idOrKey: entryId1 } : { paused: true }
  )
  const { roster: bracketAssignment2Roster } = useCompetitionTeamMembers(
    entryId2 ? { idOrKey: entryId2 } : { paused: true }
  )

  const handleFileUpload = useCallback(
    async (file: File) => {
      // Check for duplicate by comparing base64 data
      const getBase64 = (file: File) =>
        new Promise<string>(resolve => {
          const reader = new FileReader()
          reader.onload = () => resolve(reader.result as string)
          reader.readAsDataURL(file)
        })
      const newBase64 = await getBase64(file)
      const isDuplicate = screenshots.some(async s => {
        const existingBase64 = await getBase64(s.file)
        return existingBase64 === newBase64
      })
      // Since .some with async doesn't work as expected, use Promise.all
      const allBase64s = await Promise.all(
        screenshots.map(s => getBase64(s.file))
      )
      if (allBase64s.includes(newBase64)) {
        window.alert('You cannot upload the same image twice.')
        return
      }
      const url = URL.createObjectURL(file)
      const newScreenshot: UploadedScreenshot = {
        id: uuidv4(),
        url,
        file,
        isAnalyzing: false
      }
      setScreenshots(prev => [...prev, newScreenshot])
    },
    [screenshots]
  )

  // New: Analyze all screenshots at once
  const handleAnalyzeAllScreenshots = async () => {
    setScreenshots(prev =>
      prev.map(s => ({ ...s, isAnalyzing: true, error: undefined }))
    )
    try {
      // Convert all images to base64
      const base64s = await Promise.all(
        screenshots.map(async s => {
          const file = s.file
          return await new Promise<string>(resolve => {
            const reader = new FileReader()
            reader.onload = () => resolve(reader.result as string)
            reader.readAsDataURL(file)
          })
        })
      )
      // Build playerInfos as array of { id: participantId, bracketAssignmentId, screennames: [...] }
      const playerInfos = [
        ...(bracketAssignment1Roster?.map(m => ({
          id: m.participant?.id || m.id,
          bracketAssignmentId: entryId1,
          screennames: [
            m.user?.name,
            m.user?.givenName,
            m.user?.familyName
          ].filter(Boolean)
        })) || []),
        ...(bracketAssignment2Roster?.map(m => ({
          id: m.participant?.id || m.id,
          bracketAssignmentId: entryId2,
          screennames: [
            m.user?.name,
            m.user?.givenName,
            m.user?.familyName
          ].filter(Boolean)
        })) || [])
      ]
      // Send all screenshots in one request
      const apiPayload = {
        images: base64s,
        playerInfos,
        bracketAssignmentIds: [bracketAssignment1Id, bracketAssignment2Id],
        gameType: 'competitive'
      }

      console.log('[handleAnalyzeAllScreenshots] API payload:', apiPayload)
      console.log(
        '[handleAnalyzeAllScreenshots] bracketAssignment1Id:',
        entryId1
      )
      console.log(
        '[handleAnalyzeAllScreenshots] bracketAssignment2Id:',
        entryId2
      )

      const apiResponse = await fetch('/api/ai/parse-screenshot', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(apiPayload)
      })
      if (!apiResponse.ok) {
        throw new Error('Failed to analyze screenshots')
      }
      const results = await apiResponse.json() // Expect array of results
      setScreenshots(prev =>
        prev.map((s, i) => ({
          ...s,
          isAnalyzing: false,
          analysisResult: results[i],
          error: results[i]?.success
            ? undefined
            : results[i]?.error || 'Analysis failed'
        }))
      )
      // Immediately process results and go to confirm screen
      if (state.matches('uploadScreenshots')) {
        handleProcessResults(results)
      }
    } catch (error) {
      setScreenshots(prev =>
        prev.map(s => ({
          ...s,
          isAnalyzing: false,
          error: error instanceof Error ? error.message : 'Analysis failed'
        }))
      )
    }
  }

  // Update handleProcessResults to accept results
  const handleProcessResults = (results?: any[]) => {
    const usedResults = results || screenshots.map(s => s.analysisResult)
    const scoreSubmissions = usedResults
      .filter((r: any) => r && (r.success || r.winningBracketAssignmentId)) // Accept if successful OR has a winner
      .map((analysis: any, index: number) => {
        // Use the winning bracket assignment ID directly from the API response
        const winner = analysis.winningBracketAssignmentId

        // Calculate scores for numeric game scoring
        let scores: number[] | undefined
        if (bracketSettings?.numericGameScore && analysis.players) {
          // Find players for each bracket assignment
          const assignment1Player = analysis.players.find(
            (p: any) => p.bracketAssignmentId === bracketAssignment1?.id
          )
          const assignment2Player = analysis.players.find(
            (p: any) => p.bracketAssignmentId === bracketAssignment2?.id
          )
          scores = [
            assignment1Player?.score || 0,
            assignment2Player?.score || 0
          ]
        }

        return {
          ordinal: index + 1,
          winner, // bracket assignment id from API
          scores,
          fromScreenshot: {
            analysisResult: analysis,
            confidence: analysis.confidence
          }
        }
      })

    console.log('[handleProcessResults] Raw API results:', usedResults)
    console.log(
      '[handleProcessResults] bracketAssignment1:',
      bracketAssignment1
    )
    console.log(
      '[handleProcessResults] bracketAssignment2:',
      bracketAssignment2
    )
    console.log(
      '[handleProcessResults] Processed scoreSubmissions:',
      scoreSubmissions
    )
    console.log(
      '[handleProcessResults] Sending PROCESS_SCREENSHOTS event with:',
      { scoreSubmissions }
    )

    send('PROCESS_SCREENSHOTS', { scoreSubmissions })
  }

  const handleRemoveScreenshot = (screenshotId: string) => {
    setScreenshots(prev => prev.filter(s => s.id !== screenshotId))
  }

  const handleDragEnd = (result: any) => {
    if (!result.removedIndex && result.addedIndex === null) return

    const items = Array.from(screenshots)
    const [reorderedItem] = items.splice(result.removedIndex, 1)
    items.splice(result.addedIndex, 0, reorderedItem)

    // Update game slots based on new order
    const updatedItems = items.map((item, index) => ({
      ...item,
      gameSlot: index + 1
    }))

    setScreenshots(updatedItems)
  }

  const canProcessResults = screenshots.some(s => s.analysisResult?.success)
  const allAnalyzedOrErrored =
    screenshots.length > 0 &&
    screenshots.every(s => s.analysisResult || s.error)

  const successfulCount = screenshots.filter(
    s => s.analysisResult?.success
  ).length
  const errorCount = screenshots.filter(s => s.error).length
  const pendingCount = screenshots.filter(
    s => !s.analysisResult && !s.error
  ).length

  return (
    <Stack direction='column' spacing={3}>
      {/* Development Feature Notice */}
      <Alert severity='warning' icon={<RcIcon icon={['fal', 'flask']} />}>
        <Typography variant='body2'>
          <strong>Development Feature:</strong> AI-powered screenshot analysis
          is currently in development. This feature may produce unexpected
          results and is being actively improved.
        </Typography>
      </Alert>

      {/* Summary */}
      {screenshots.length > 0 && (
        <Alert severity='info' icon={<RcIcon icon={['fal', 'info-circle']} />}>
          <Typography variant='body2'>
            <RcTrans
              i18nKey='competition:screenshot-summary'
              tOptions={{
                total: screenshots.length,
                success: successfulCount,
                failed: errorCount,
                pending: pendingCount
              }}
            />
          </Typography>
        </Alert>
      )}
      {/* Instructions */}
      <Alert severity='info' icon={<RcIcon icon={['fal', 'camera']} />}>
        <Typography variant='body2'>
          <RcTrans
            i18nKey='competition:screenshot-upload-instructions'
            tOptions={{ slots: requiredSlots }}
          />
        </Typography>
      </Alert>
      {/* File Upload */}
      <Card>
        <CardContent>
          <Stack direction='column' spacing={2}>
            <Typography variant='subtitle1'>
              <RcTrans i18nKey='competition:upload-screenshots' />
            </Typography>
            <RcFileInput
              saveAs={`match-screenshot-${uuidv4()}`}
              onComplete={handleFileUpload}
              allowAnyFile={false}
              skipCropping={true}
            />
          </Stack>
        </CardContent>
      </Card>
      {/* Screenshot Grid with Drag & Drop */}
      {screenshots.length > 0 && (
        <Card>
          <CardContent>
            <Stack direction='column' spacing={2}>
              <Typography variant='subtitle1'>
                <RcTrans i18nKey='competition:uploaded-screenshots' />
                <Chip
                  label={`${screenshots.length}/${requiredSlots}`}
                  size='small'
                  sx={{ ml: 1 }}
                />
              </Typography>
              <Container onDrop={handleDragEnd}>
                {screenshots.map((screenshot, index) => (
                  <Draggable key={screenshot.id}>
                    <Box mb={2}>
                      <Card
                        sx={{
                          width: '100%',
                          position: 'relative',
                          border: screenshot.analysisResult?.success
                            ? '2px solid green'
                            : screenshot.error
                              ? '2px solid red'
                              : '1px solid grey',
                          boxShadow: 2,
                          cursor: 'grab'
                        }}
                      >
                        {/* Game Slot Number */}
                        <Chip
                          label={`Game ${screenshot.gameSlot || index + 1}`}
                          size='small'
                          sx={{
                            position: 'absolute',
                            top: 8,
                            left: 8,
                            zIndex: 1
                          }}
                        />
                        {/* Remove Button */}
                        <IconButton
                          size='small'
                          onClick={() => handleRemoveScreenshot(screenshot.id)}
                          sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            zIndex: 1,
                            bgcolor: 'background.paper'
                          }}
                        >
                          <RcIcon icon={['fal', 'times']} size='xs' />
                        </IconButton>
                        {/* Screenshot Preview */}
                        <Box
                          sx={{
                            aspectRatio: '16/9',
                            position: 'relative',
                            width: '100%'
                          }}
                        >
                          <img
                            src={screenshot.url}
                            alt={`Game ${index + 1} screenshot`}
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                              borderRadius: 8,
                              display: 'block'
                            }}
                            draggable={false}
                            onDragStart={e => e.preventDefault()}
                          />
                          {screenshot.isAnalyzing && (
                            <Box
                              sx={{
                                position: 'absolute',
                                bottom: 0,
                                left: 0,
                                right: 0
                              }}
                            >
                              <LinearProgress />
                            </Box>
                          )}
                        </Box>
                        {/* Analysis Result or Action */}
                        <CardContent sx={{ pt: 1 }}>
                          {screenshot.isAnalyzing ? (
                            <Typography
                              variant='caption'
                              color='text.secondary'
                            >
                              <RcTrans i18nKey='competition:analyzing-screenshot' />
                            </Typography>
                          ) : screenshot.analysisResult ? (
                            <Stack direction='column' spacing={1}>
                              <Typography
                                variant='caption'
                                color='success.main'
                              >
                                <Box
                                  component='span'
                                  sx={{ mr: 1, display: 'inline-block' }}
                                >
                                  <RcIcon icon={['fal', 'check']} size='xs' />
                                </Box>
                                <RcTrans
                                  i18nKey='competition:analysis-complete'
                                  tOptions={{
                                    confidence: Math.round(
                                      screenshot.analysisResult.confidence * 100
                                    )
                                  }}
                                />
                              </Typography>
                              {screenshot.analysisResult.players?.map(
                                (player: any, i: number) => (
                                  <Typography key={i} variant='caption'>
                                    {player.detectedName}: {player.score}
                                  </Typography>
                                )
                              )}
                            </Stack>
                          ) : screenshot.error ? (
                            <Stack
                              direction='row'
                              spacing={1}
                              alignItems='center'
                            >
                              <Typography variant='caption' color='error.main'>
                                <Box
                                  component='span'
                                  sx={{ mr: 1, display: 'inline-block' }}
                                >
                                  <RcIcon
                                    icon={['fal', 'exclamation-triangle']}
                                    size='xs'
                                  />
                                </Box>
                                {screenshot.error}
                              </Typography>
                            </Stack>
                          ) : null}
                        </CardContent>
                      </Card>
                    </Box>
                  </Draggable>
                ))}
              </Container>
            </Stack>
          </CardContent>
        </Card>
      )}
      {/* Flow Actions */}
      <MatchResultFlowActions
        machine={machine}
        title={<RcTrans i18nKey='competition:upload-screenshots-title' />}
        topContent={
          <Box sx={{ p: 7 }}>
            <MatchCard
              match={match}
              competition={state.context.competition}
              hideViewMatch
            />
          </Box>
        }
        screenshots={screenshots}
        onAnalyzeAllScreenshots={handleAnalyzeAllScreenshots}
      />
    </Stack>
  )
}
